// Firebase Journey Service
// =========================
// CRUD operations for learning journeys (læringsforløb)

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { getDocument, updateDocument, queryDocuments, where, orderBy } from './base';
import { LIMITS } from '../../config/limits';
import i18n from '../../i18n';

/**
 * Create a new learning journey in Firestore
 * @param data Learning journey data (without id)
 * @returns Result with success status and created journey id
 */
export const createLearningJourney = async (
  data: {
    userId: string;
    subject: string;
    title?: string;
    examDate?: Date | string;
    examFormat?: 'mundtlig' | 'skriftlig' | 'multiple_choice';
    weeklyStudyHours?: number;
  }
) => {
  try {
    // Check active journey count limit per user
    const countQuery = query(
      collection(db, 'learningJourneys'),
      where('userId', '==', data.userId),
      where('status', '==', 'active')
    );
    const countSnapshot = await getDocs(countQuery);
    if (countSnapshot.size >= LIMITS.MAX_ACTIVE_JOURNEYS) {
      return {
        success: false,
        error: i18n.t('common:limits.maxJourneysReached'),
      };
    }

    // Build journey data, filtering out undefined values (Firebase rejects undefined)
    // Note: title is required by Firestore rules, defaults to subject if not provided
    const journeyData: Record<string, unknown> = {
      userId: data.userId,
      subject: data.subject,
      title: data.title || data.subject, // Required by Firestore rules - default to subject
      examDate: data.examDate
        ? (data.examDate instanceof Date ? Timestamp.fromDate(data.examDate) : Timestamp.fromDate(new Date(data.examDate)))
        : null,
      topicIds: [],
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Only add optional fields if they have values
    if (data.examFormat !== undefined) journeyData.examFormat = data.examFormat;
    if (data.weeklyStudyHours !== undefined) journeyData.weeklyStudyHours = data.weeklyStudyHours;

    const docRef = await addDoc(collection(db, 'learningJourneys'), journeyData);
    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all learning journeys for a user
 * @param userId The user's ID
 * @returns Result with success status and array of journeys
 */
export const getLearningJourneys = async (userId: string) => {
  return queryDocuments<{
    id: string;
    userId: string;
    subject: string;
    title?: string;
    examDate?: Date | string;
    examFormat?: 'mundtlig' | 'skriftlig' | 'multiple_choice';
    weeklyStudyHours?: number;
    topicIds?: string[];
    analyzedMaterialIds?: string[]; // Feature #180: Track which materials were analyzed
    status?: 'active' | 'completed' | 'archived';
    createdAt?: Date | string;
    updatedAt?: Date | string;
  }>(
    'learningJourneys',
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
};

/**
 * Get a single learning journey by ID
 * @param journeyId The journey's ID
 * @returns Result with success status and journey data
 */
export const getLearningJourney = async (journeyId: string) => {
  return getDocument<{
    id: string;
    userId: string;
    subject: string;
    title?: string;
    examDate?: Date | string;
    examFormat?: 'mundtlig' | 'skriftlig' | 'multiple_choice';
    weeklyStudyHours?: number;
    topicIds?: string[];
    analyzedMaterialIds?: string[]; // Feature #180: Track which materials were analyzed
    status?: 'active' | 'completed' | 'archived';
    createdAt?: Date | string;
    updatedAt?: Date | string;
  }>('learningJourneys', journeyId);
};

/**
 * Update an existing learning journey
 * @param journeyId The journey's ID
 * @param data Partial journey data to update
 * @returns Result with success status
 */
export const updateLearningJourney = async (
  journeyId: string,
  data: Partial<{
    subject: string;
    title: string;
    examDate: Date | string | null;
    examFormat: 'mundtlig' | 'skriftlig' | 'multiple_choice';
    weeklyStudyHours: number;
    topicIds: string[];
    analyzedMaterialIds: string[]; // Feature #180: Track which materials were analyzed
    status: 'active' | 'completed' | 'archived';
  }>
) => {
  // Build update data, filtering out undefined values (Firebase rejects undefined)
  const updateData: Record<string, unknown> = {};

  // Copy over defined values only
  if (data.subject !== undefined) updateData.subject = data.subject;
  if (data.title !== undefined) updateData.title = data.title;
  if (data.weeklyStudyHours !== undefined) updateData.weeklyStudyHours = data.weeklyStudyHours;
  if (data.topicIds !== undefined) updateData.topicIds = data.topicIds;
  if (data.analyzedMaterialIds !== undefined) updateData.analyzedMaterialIds = data.analyzedMaterialIds;
  if (data.status !== undefined) updateData.status = data.status;
  if (data.examFormat !== undefined) updateData.examFormat = data.examFormat;

  // Convert examDate to Timestamp if it's a Date or string, or null if explicitly set to null
  if (data.examDate !== undefined) {
    updateData.examDate = data.examDate
      ? (data.examDate instanceof Date ? Timestamp.fromDate(data.examDate) : Timestamp.fromDate(new Date(data.examDate)))
      : null;
  }

  return updateDocument('learningJourneys', journeyId, updateData);
};

/**
 * Delete a learning journey and optionally clean up related data
 * @param journeyId The journey's ID
 * @param cleanupTopics Whether to unlink topics from this journey (default: true)
 * @returns Result with success status
 */
export const deleteLearningJourney = async (
  journeyId: string,
  _cleanupTopics: boolean = true
) => {
  try {
    // If cleanupTopics is true, we should unlink topics from this journey
    // This will be implemented when topics are migrated to Firestore
    // For now, just delete the journey document

    await deleteDoc(doc(db, 'learningJourneys', journeyId));
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
